import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import api from "@/lib/api";
import { CATEGORIES, COUNTRIES } from "@/lib/content";
import RazorpayCheckout from "@/components/RazorpayCheckout";
import SEO from "@/components/SEO";
import Turnstile from "@/components/Turnstile";

// Country dial codes — extend as needed
const DIAL_CODES = [
  { code: "+91",  label: "🇮🇳 India (+91)" },
  { code: "+86",  label: "🇨🇳 China (+86)" },
  { code: "+1",   label: "🇺🇸 US / Canada (+1)" },
  { code: "+44",  label: "🇬🇧 UK (+44)" },
  { code: "+61",  label: "🇦🇺 Australia (+61)" },
  { code: "+65",  label: "🇸🇬 Singapore (+65)" },
  { code: "+971", label: "🇦🇪 UAE (+971)" },
  { code: "+66",  label: "🇹🇭 Thailand (+66)" },
  { code: "+81",  label: "🇯🇵 Japan (+81)" },
  { code: "+82",  label: "🇰🇷 South Korea (+82)" },
  { code: "+49",  label: "🇩🇪 Germany (+49)" },
  { code: "+33",  label: "🇫🇷 France (+33)" },
  { code: "+34",  label: "🇪🇸 Spain (+34)" },
  { code: "+31",  label: "🇳🇱 Netherlands (+31)" },
  { code: "+7",   label: "🇷🇺 Russia (+7)" },
  { code: "+55",  label: "🇧🇷 Brazil (+55)" },
  { code: "+52",  label: "🇲🇽 Mexico (+52)" },
  { code: "+27",  label: "🇿🇦 South Africa (+27)" },
  { code: "+20",  label: "🇪🇬 Egypt (+20)" },
  { code: "+977", label: "🇳🇵 Nepal (+977)" },
  { code: "+975", label: "🇧🇹 Bhutan (+975)" },
  { code: "+880", label: "🇧🇩 Bangladesh (+880)" },
  { code: "+94",  label: "🇱🇰 Sri Lanka (+94)" },
];

const CURRENCIES = ["USD", "INR", "EUR", "GBP", "AED", "CNY", "AUD", "SGD", "NPR", "BTN"];

const STEPS = ["Contact", "Address", "Requirement"];

export default function SubmitRFQ() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const [done, setDone] = useState(null);
  const nav = useNavigate();

  const [form, setForm] = useState({
    // Contact
    name: "", company: "", email: "", designation: "",
    phone_country_code: "+91", phone_number: "",
    whatsapp: "", wechat_id: "",
    // Address
    address_line1: "", address_line2: "",
    city: "", state: "", country: "India", postal_code: "",
    // Requirement
    product_category: "", project_description: "",
    quantity: "", timeline: "",
    est_value: "", est_value_currency: "USD",
    source: "website",
    referred_by: "",
  });

  const set = (k, v) => setForm({ ...form, [k]: v });

  const canContinue = () => {
    if (step === 0) {
      return form.name.trim().length >= 2
        && form.company.trim().length >= 1
        && /^\S+@\S+\.\S+$/.test(form.email)
        && form.phone_number.trim().length >= 4;
    }
    if (step === 1) return form.city.trim().length >= 1 && form.country.trim().length >= 2;
    if (step === 2) return form.project_description.trim().length >= 10;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form, turnstile_token: tsToken };
      if (payload.est_value === "") payload.est_value = null;
      else payload.est_value = Number(payload.est_value);
      const r = await api.post("/enquiries/public", payload);
      setDone({ code: r.data.code, id: r.data.id });
      toast.success("Enquiry received. A senior sourcing lead will contact you within one working day.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map((d) => d.msg || d).join(", "));
      } else {
        toast.error(detail || "Submission failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white py-24 min-h-[80vh]" data-testid="rfq-success">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-16 h-16 border-2 border-[#B8965A] rounded-full flex items-center justify-center mx-auto mb-8">
            <Check size={28} className="text-[#B8965A]" />
          </div>
          <h1 className="font-serif text-[#1C3E5F] text-5xl mb-6">Enquiry Received.</h1>
          <p className="text-[17px] text-[#1C3E5F]/75 leading-relaxed mb-4">
            Reference <strong className="text-[#B8965A]" data-testid="rfq-reference">{done.code}</strong>
          </p>
          <p className="text-[#1C3E5F]/70 mb-8">
            A senior sourcing lead will reach out within one working day with next steps and a preliminary factory shortlist.
          </p>

          <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-6 mb-8 text-left">
            <div className="eyebrow text-[#B8965A] text-[10px] mb-2">Optional · Priority Engagement</div>
            <h3 className="font-serif text-[#1C3E5F] text-2xl mb-2">Lock priority slot with a refundable retainer</h3>
            <p className="text-sm text-[#1C3E5F]/70 mb-4">
              Pay a refundable <strong>USD 550</strong> engagement retainer to lock priority queue access. Adjusted against your final invoice; refunded in full if we cannot service your requirement.
            </p>
            <RazorpayCheckout
              flow="rfq_deposit"
              amount={550}
              currency="USD"
              purpose={`Enquiry ${done.code}, engagement retainer`}
              rfqId={done.id}
              customerName={form.name}
              customerEmail={form.email}
              customerContact={`${form.phone_country_code}${form.phone_number}`}
              testId="rfq-deposit-btn"
              className="btn-primary !py-3 !px-6 !text-[12px]"
            >
              Pay USD 550 retainer
            </RazorpayCheckout>
          </div>

          <button onClick={() => nav("/")} className="btn-outline" data-testid="rfq-return-home">Return Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-[90vh] py-16" data-testid="rfq-page">
      <SEO
        title="Submit an Enquiry · China Sourcing Quote · Fortune Sourcings"
        description="Submit a sourcing enquiry to Fortune Sourcings — container-load and project-based China sourcing. A senior sourcing lead responds within one working day."
        path="/submit-rfq"
      />
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-6">
          <span className="gold-rule" /><span className="eyebrow text-[#1C3E5F]">New Sourcing Enquiry</span>
        </div>
        <h1 className="font-serif text-[#1C3E5F] text-5xl mb-4">Tell us about your requirement.</h1>
        <p className="text-[#1C3E5F]/70 mb-10">
          Container-load and project-based engagements only. A senior sourcing lead responds within one working day.
        </p>

        <div className="flex items-center mb-10">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 border flex items-center justify-center text-xs font-semibold ${i <= step ? "bg-[#B8965A] border-[#B8965A] text-[#1C3E5F]" : "border-[#E2E8F0] text-[#6B7280]"}`}>{i + 1}</div>
                <span className={`text-xs uppercase tracking-[0.15em] hidden md:inline ${i === step ? "text-[#1C3E5F] font-semibold" : "text-[#6B7280]"}`}>{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="flex-1 h-px bg-[#E2E8F0] mx-3" />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white border border-[#E2E8F0] p-8 md:p-10">
          {step === 0 && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="Full Name" required>
                  <input data-testid="rfq-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="fs-input" />
                </FormField>
                <FormField label="Company" required>
                  <input data-testid="rfq-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="fs-input" />
                </FormField>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="Email" required>
                  <input data-testid="rfq-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="fs-input" />
                </FormField>
                <FormField label="Designation">
                  <input data-testid="rfq-designation" value={form.designation} onChange={(e) => set("designation", e.target.value)} className="fs-input" placeholder="e.g. Procurement Head" />
                </FormField>
              </div>
              <FormField label="Phone" required hint="Include country dial code">
                <div className="flex gap-2">
                  <select data-testid="rfq-phone-cc" value={form.phone_country_code} onChange={(e) => set("phone_country_code", e.target.value)} className="fs-input !w-40 shrink-0">
                    {DIAL_CODES.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
                  </select>
                  <input data-testid="rfq-phone" value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)} className="fs-input flex-1" placeholder="Number only" inputMode="numeric" />
                </div>
              </FormField>
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="WhatsApp" hint="Optional — if different from phone">
                  <input data-testid="rfq-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="fs-input" placeholder="+91 98xxxxxxxx" />
                </FormField>
                <FormField label="WeChat ID" hint="Optional">
                  <input data-testid="rfq-wechat" value={form.wechat_id} onChange={(e) => set("wechat_id", e.target.value)} className="fs-input" placeholder="wxid_xxxxx" />
                </FormField>
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-6">
              <FormField label="Address Line 1">
                <input data-testid="rfq-addr1" value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} className="fs-input" placeholder="Building / street" />
              </FormField>
              <FormField label="Address Line 2">
                <input data-testid="rfq-addr2" value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} className="fs-input" placeholder="Area / landmark" />
              </FormField>
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="City" required>
                  <input data-testid="rfq-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="fs-input" />
                </FormField>
                <FormField label="State / Province">
                  <input data-testid="rfq-state" value={form.state} onChange={(e) => set("state", e.target.value)} className="fs-input" />
                </FormField>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="Country" required>
                  <select data-testid="rfq-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="fs-input">
                    {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FormField>
                <FormField label="Postal / ZIP Code">
                  <input data-testid="rfq-postal" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} className="fs-input" />
                </FormField>
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-6">
              <FormField label="Product Category">
                <select data-testid="rfq-product-category" value={form.product_category} onChange={(e) => set("product_category", e.target.value)} className="fs-input">
                  <option value="">Select category…</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </FormField>
              <FormField label="Project Details & Specifications" required hint="10+ characters">
                <textarea data-testid="rfq-details" value={form.project_description} onChange={(e) => set("project_description", e.target.value)} rows={6}
                  placeholder="Describe exactly what you need, materials, dimensions, finishes, technical specifications, reference images…"
                  className="fs-input !py-3 resize-none" />
              </FormField>
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="Quantity / Volume">
                  <input data-testid="rfq-qty" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} className="fs-input" placeholder="e.g. 1×40HQ, 5,000 pcs" />
                </FormField>
                <FormField label="Required Timeline">
                  <input data-testid="rfq-timeline" value={form.timeline} onChange={(e) => set("timeline", e.target.value)} className="fs-input" placeholder="e.g. 90 days to destination port" />
                </FormField>
              </div>
              <FormField label="Estimated Budget" hint="Approximate — indicative only">
                <div className="flex gap-2">
                  <select data-testid="rfq-ccy" value={form.est_value_currency} onChange={(e) => set("est_value_currency", e.target.value)} className="fs-input !w-28 shrink-0">
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input data-testid="rfq-value" type="number" value={form.est_value} onChange={(e) => set("est_value", e.target.value)} className="fs-input flex-1" placeholder="100000" />
                </div>
              </FormField>
              <div className="grid md:grid-cols-2 gap-5">
                <FormField label="How did you hear about us?">
                  <select data-testid="rfq-source" value={form.source} onChange={(e) => set("source", e.target.value)} className="fs-input">
                    <option value="website">Website / Search</option>
                    <option value="referral">Referral</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="ad">Advertisement</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="event">Event / Trade Show</option>
                    <option value="other">Other</option>
                  </select>
                </FormField>
                <FormField label="Referred by (if any)">
                  <input data-testid="rfq-referred" value={form.referred_by} onChange={(e) => set("referred_by", e.target.value)} className="fs-input" placeholder="Name or company" />
                </FormField>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-between">
          <button data-testid="rfq-prev" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="btn-outline text-[#1C3E5F] border-[#1C3E5F] hover:bg-[#1C3E5F] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed">
            <ArrowLeft size={16} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button data-testid="rfq-next" onClick={() => canContinue() && setStep(step + 1)} disabled={!canContinue()} className="btn-dark disabled:opacity-40 disabled:cursor-not-allowed">
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <div className="flex flex-col items-end gap-2">
              <Turnstile action="rfq" onVerify={setTsToken} />
              <button data-testid="rfq-submit" onClick={submit} disabled={!canContinue() || submitting} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">
                {submitting ? "Submitting…" : "Submit Enquiry"} <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FormField({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="flex justify-between items-baseline mb-2">
        <span className="text-[12px] uppercase tracking-[0.18em] font-semibold text-[#1C3E5F]">
          {label}{required && <span className="text-[#B8965A] ml-1">*</span>}
        </span>
        {hint && <span className="text-xs text-[#6B7280]">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
